import formatDecimal, {
  isValidDecimal,
  parseFormattedFloat,
} from '@paradigm/utils/formatDecimal';
import { UnifiedInstruments } from '#/unified-rfqs/ui/instruments/types';
import { EMPTY_GREEKS, Greeks } from '../../entities/greeks';
import { DraftLeg, DraftOptionLeg } from './types';
import { isOptionLeg } from './leg-functions';
import { greeksView } from './views/option-leg-greeks';
import { spansMultipleProducts } from './multi-product';

export default function calculateGreeks(
  legs: readonly DraftLeg[],
  auction: string | null,
  instruments: UnifiedInstruments['optionInstruments'],
): Greeks {
  const optionLegs = legs.filter((leg) =>
    isOptionLeg(leg),
  ) as readonly DraftOptionLeg[];

  const greeksResult = optionLegs.reduce((acc, leg) => {
    const greeks = greeksView(
      leg.productCode,
      leg.optionKind,
      leg.expiration,
      leg.strike,
      instruments,
    );
    if (greeks == null) return acc;

    const quantity = calculateQuantity(auction, leg.quantity);
    const sideMultiplier = leg.action === 'BUY' ? 1 : -1;
    const ratio = isValidDecimal(leg.ratio)
      ? parseFormattedFloat(leg.ratio)
      : 0;
    const size =
      leg.quantity !== undefined && auction === 'RFQ' ? quantity : ratio;
    const sumGreek = (total?: string, cur?: string) => {
      return formatDecimal(
        (
          parseValue(total) +
          parseValue(cur) * sideMultiplier * size
        ).toString(),
      );
    };

    return {
      delta: sumGreek(acc.delta, greeks.delta),
      gamma: sumGreek(acc.gamma, greeks.gamma),
      mark_price: sumGreek(acc.mark_price, greeks.mark_price),
      theta: sumGreek(acc.theta, greeks.theta),
      vega: sumGreek(acc.vega, greeks.vega),
    };
  }, EMPTY_GREEKS);

  // A BTC option's delta/gamma and a HYPE option's delta/gamma are per-coin
  // sensitivities with no shared unit, so summing them across products is
  // meaningless (vega/theta/mark_price are USDC-denominated and genuinely
  // additive, so those keep summing). Mirrors the backend and the Paradex
  // client's own delta/gamma handling.
  if (spansMultipleProducts(legs)) {
    return { ...greeksResult, delta: '', gamma: '' };
  }
  return greeksResult;
}

function calculateQuantity(auction: string | null, quantity?: string) {
  return quantity !== undefined && auction === 'RFQ' && isValidDecimal(quantity)
    ? parseFormattedFloat(quantity)
    : 1;
}

const parseValue = (value?: string) => {
  if (value == null || value === '') return 0;
  return parseFormattedFloat(value);
};
